/* TrackDNA — saved-vehicle bar.
 *
 * Fills and reveals the bar that sections/trackdna-vehicle-bar.liquid renders empty.
 *
 * WHERE THE VEHICLE COMES FROM. EasySearch keeps the active vehicle filter in
 * `easysearch-preselect`, written when a search is run as its `curIndex` with commas
 * swapped for `preSelectDelimiter`. It is stored through the app's own
 * getCookie/setCookie pair, which are localStorage-backed and wrap the value in
 * `{value, expires}`. We therefore read it through `easysearch.getCookie(...)` rather
 * than touching localStorage ourselves — that accessor is what honours the expiry.
 *
 * IT EXPIRES. `defaultStoragePeriod` is 3600s, so the app drops the filter after an
 * hour and this bar disappears with it. That is the app's filter lifetime, not a bug
 * here. The 10-year store is `easysearch-garage-items`, a different thing: the saved
 * garage list, not the active filter.
 *
 * LEVEL COUNT IS NOT HARDCODED. It joins whatever non-empty values the app returns, so
 * if a fourth Trim field is added in the EasySearch admin the bar picks it up with no
 * change here.
 *
 * IT FAILS CLOSED. No app, no state, or an empty vehicle leaves the bar hidden. It must
 * never render "Showing parts for:" with nothing after it.
 */
(function () {
  "use strict";

  var KEY = "easysearch-preselect";

  /* The delimiter EasySearch writes between levels. Read from the app when it is on the
     page; this literal is the fallback for pages where it is not. Confirmed against the
     stored value: {"value":"2027<DELIM>BMW<DELIM>M5<DELIM>","expires":"<ms>"} */
  var DELIMITER = "easysearch-preselect-delimiter";

  function bars() {
    return Array.prototype.slice.call(document.querySelectorAll("[data-td-vehicle-bar]"));
  }

  /* EasySearch's own accessor, when the app is on this page. */
  function readViaApp() {
    var es = window.easysearch;
    if (!es || typeof es.getCookie !== "function") return null;
    try {
      return { raw: es.getCookie(KEY), delimiter: es.preSelectDelimiter || DELIMITER };
    } catch (e) {
      return null;
    }
  }

  /* The app's script is only loaded where its block is placed — on this store that is
     Home alone. The bar lives in the header group and must work sitewide, so on every
     other page it reads the same localStorage entry directly.
     The app stores {value, expires} with expires in ms, so the expiry is honoured here
     too: a stale entry is treated as no vehicle rather than shown as a current one. */
  function readViaStorage() {
    var raw;
    try {
      raw = window.localStorage.getItem(KEY);
    } catch (e) {
      return null; // private mode, blocked storage
    }
    if (!raw) return null;

    var parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (e) {
      return null;
    }
    if (!parsed || typeof parsed.value !== "string") return null;

    var expires = parseInt(parsed.expires, 10);
    if (expires && Date.now() > expires) return null;

    return { raw: parsed.value, delimiter: DELIMITER };
  }

  function readVehicle() {
    var state = readViaApp() || readViaStorage();
    if (!state || !state.raw || typeof state.raw !== "string") return "";

    var parts = state.raw.split(state.delimiter).filter(function (v) {
      return v && String(v).trim() !== "";
    });

    return parts.join(" ").trim();
  }

  function render() {
    var vehicle = readVehicle();

    bars().forEach(function (bar) {
      var slot = bar.querySelector("[data-td-vehicle]");
      if (!slot) return;

      if (!vehicle) {
        bar.hidden = true;
        slot.textContent = "";
        return;
      }

      slot.textContent = vehicle;
      bar.hidden = false;
    });
  }

  /* The change link points at /pages/shop-by-vehicle, and trackdna-vehicle-modal.js
     intercepts every link to that URL store-wide — opening the popup, or scrolling to a
     form already on the page. Nothing to wire here; without that script the link simply
     navigates, which is the correct fallback. */

  function boot() {
    render();
  }

  /* The app fires these on its own document. `easysearch_params_saved` is the one that
     matters — it lands when a search is committed, which is when a vehicle becomes set. */
  ["easysearch_ready", "easysearch_rendered", "easysearch_params_saved", "easysearch_select_change", "easysearch_garage_rendered"].forEach(
    function (name) {
      document.addEventListener(name, boot);
    }
  );

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  /* Shopify's theme editor re-renders a section without a page load. */
  document.addEventListener("shopify:section:load", boot);
})();

// Launch-quality fix, 25 Oct 2026. EasySearch renders its clear-filter link with no text,
// so a screen reader announces nothing. Label it whenever the app (re)renders it.
(function labelEasySearchClear() {
  const label = document.documentElement.dataset.tdClearVehicle;
  if (!label) return;
  const apply = () => document.querySelectorAll(".easysearch-btn-clean:not([aria-label])")
    .forEach((el) => { if (!el.textContent.trim()) el.setAttribute("aria-label", label); });
  let queued = false;
  apply();
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => { queued = false; apply(); });
  }).observe(document.body, { childList: true, subtree: true });
})();
